/**
 * Execution Context Implementation
 *
 * Manages state during workflow execution including step outputs,
 * execution logs, and metadata.
 *
 * @packageDocumentation
 */

import type {
  ExecutionContext,
  StepExecutionLog,
  RunOptions,
} from '../types/execution';
import type { WorkflowDefinition } from '../types/workflow';
import { generateRunId } from '../utils/uuid';

/**
 * Implementation of ExecutionContext interface.
 *
 * Handles all state management during a workflow run.
 * This class does NOT perform any workflow logic - it's purely for bookkeeping.
 *
 * @remarks
 * - Thread-safe for single-threaded execution (Node.js)
 * - Immutable after finalize() is called
 * - All outputs are stored by reference (no deep cloning)
 *
 * @example
 * ```typescript
 * const context = new ExecutionContextImpl(workflow, userInput, options);
 *
 * // During execution
 * context.setOutput('extract', { invoice: { total: 1500 } });
 * context.appendLog(extractLog);
 *
 * const extractOutput = context.getOutput('extract');
 *
 * // After execution
 * context.finalize();
 * ```
 */
export class ExecutionContextImpl implements ExecutionContext {
  public readonly runId: string;
  public readonly workflowName: string;
  public readonly startTime: string;
  public endTime?: string;
  public readonly initialInput: any;
  public readonly stepOutputs: Map<string, any>;
  public readonly logs: StepExecutionLog[];
  public readonly options: RunOptions;

  private finalized: boolean = false;

  /**
   * Creates a new execution context for a workflow run.
   *
   * @param workflow - The workflow being executed
   * @param initialInput - Input provided by the user
   * @param options - Runtime options for this execution
   * @param runId - Optional custom run ID (generated if not provided)
   */
  constructor(
    workflow: WorkflowDefinition,
    initialInput: any,
    options: RunOptions = {},
    runId?: string
  ) {
    this.runId = runId || generateRunId();
    this.workflowName = workflow.workflowName;
    this.startTime = new Date().toISOString();
    this.initialInput = initialInput;
    this.stepOutputs = new Map();
    this.logs = [];
    this.options = options;
  }

  /**
   * Stores the output of a completed step.
   *
   * @param stepName - Name of the step
   * @param output - Output value from the step
   * @throws {Error} If context is already finalized
   */
  setOutput(stepName: string, output: any): void {
    if (this.finalized) {
      throw new Error('Cannot set output on finalized context');
    }
    this.stepOutputs.set(stepName, output);
  }

  /**
   * Retrieves the output of a previously executed step.
   *
   * @param stepName - Name of the step
   * @returns Output value, or undefined if step hasn't executed
   */
  getOutput(stepName: string): any {
    return this.stepOutputs.get(stepName);
  }

  /**
   * Checks if a step has been executed and has output.
   *
   * @param stepName - Name of the step
   * @returns True if step has output stored
   */
  hasOutput(stepName: string): boolean {
    return this.stepOutputs.has(stepName);
  }

  /**
   * Appends a step execution log to the context.
   *
   * @param log - Step execution log entry
   * @throws {Error} If context is already finalized
   */
  appendLog(log: StepExecutionLog): void {
    if (this.finalized) {
      throw new Error('Cannot append log to finalized context');
    }
    this.logs.push(log);
  }

  /**
   * Gets all execution logs in order.
   *
   * @returns Array of step execution logs
   */
  getLogs(): StepExecutionLog[] {
    return [...this.logs];
  }

  /**
   * Gets the number of steps that have been executed.
   *
   * @returns Number of logs
   */
  getStepCount(): number {
    return this.logs.length;
  }

  /**
   * Finalizes the context by setting the end time.
   *
   * After finalization, no more outputs or logs can be added.
   */
  finalize(): void {
    if (!this.finalized) {
      this.endTime = new Date().toISOString();
      this.finalized = true;
    }
  }

  /**
   * Checks if the context has been finalized.
   *
   * @returns True if finalized
   */
  isFinalized(): boolean {
    return this.finalized;
  }

  /**
   * Gets the total duration of the workflow run in milliseconds.
   *
   * @returns Duration in ms, or undefined if not finalized
   */
  getDuration(): number | undefined {
    if (!this.endTime) {
      return undefined;
    }
    return new Date(this.endTime).getTime() - new Date(this.startTime).getTime();
  }

  /**
   * Creates a summary object of the execution context.
   *
   * @returns Summary object with key metrics
   */
  getSummary(): {
    runId: string;
    workflowName: string;
    startTime: string;
    endTime?: string;
    duration?: number;
    stepCount: number;
    outputCount: number;
    finalized: boolean;
  } {
    return {
      runId: this.runId,
      workflowName: this.workflowName,
      startTime: this.startTime,
      endTime: this.endTime,
      duration: this.getDuration(),
      stepCount: this.logs.length,
      outputCount: this.stepOutputs.size,
      finalized: this.finalized,
    };
  }
}
